import { isPrivateClientStrategyAlias } from "../../utils/proposalUtils";
import { isInvestmentAmountWithinLimits } from "./ispFundingThresholds";

const ELIGIBLE_EXISTING_ACTIONS = new Set([
  "Strategy Change",
  "Risk Tolerance Update",
  "Model Change",
  "Fee Update",
]);

export const STANDARD_ACCOUNT_GOALS = [
  "Education Funding",
  "Retirement Funding",
  "Estate Planning",
  "Funding for another goal",
];

export const MMMS_ACCOUNT_GOALS = [
  "Education Funding",
  "Retirement Funding",
  "Estate Planning",
  "Reserve for unplanned expenses",
  "Accumulated assets for future investment in the market",
  "Interim reserve for assets due to market volatility",
  "Other",
];

export const supportsStandardRegistrationType = (
  requestType,
  existingRequestAction,
) =>
  requestType === "New" ||
  (requestType === "Existing" &&
    ELIGIBLE_EXISTING_ACTIONS.has(existingRequestAction));

export const isFusionRegistrationStrategy = (strategy) =>
  String(strategy || "").toLowerCase().includes("fusion");

export const isStandardRegistrationStrategy = (
  strategy,
  proposalTypes = [],
) =>
  !!String(strategy || "").trim() &&
  !isFusionRegistrationStrategy(strategy) &&
  !isPrivateClientStrategyAlias(strategy, proposalTypes);

export const createStandardRegistrationFields = (source = {}) => ({
  initialInvestmentAmount: String(source.initialInvestmentAmount || ""),
  accountNetWorthPct: String(source.accountNetWorthPct ?? ""),
  accountIncomeNeed: String(source.accountIncomeNeed || ""),
  accountIncomeAmount: String(source.accountIncomeAmount || ""),
  accountIncomeStartDate: String(source.accountIncomeStartDate || ""),
  accountIncomeEndDate: String(source.accountIncomeEndDate || ""),
  accountIncomeFrequency: String(source.accountIncomeFrequency || ""),
  accountGoal: Array.isArray(source.accountGoal)
    ? [...source.accountGoal]
    : String(source.accountGoal || ""),
  accountGoalOther: String(source.accountGoalOther || ""),
  bondType: ["Taxable", "Municipal"].includes(source.bondType)
    ? source.bondType
    : "",
  bondTypePreferenceProvided:
    source.bondTypePreferenceProvided === true ||
    source.bondTypePreferenceProvided === "true",
});

export const getStandardRegistrationValue = (source = {}) => {
  const storedRegistration = /^registration\d+$/i.test(
    String(source.registration || "").trim(),
  )
    ? ""
    : source.registration;
  return String(
    source.value || source.registrationName || storedRegistration || "",
  );
};

export const createStandardRegistrationRow = (
  source = {},
  index = 0,
) => ({
  ...source,
  registration: `registration${index}`,
  accountOwner: String(source.accountOwner || source.name || ""),
  value: getStandardRegistrationValue(source),
  percentage: String(source.percentage ?? "100"),
  ...createStandardRegistrationFields(source),
});

export const normalizeStandardRegistrationRows = (
  registrations = [],
  firstRowDefaults = {},
) => {
  const rows = Array.isArray(registrations) && registrations.length > 0
    ? registrations
    : [{}];
  return rows.map((registration, index) =>
    createStandardRegistrationRow(
      index === 0
        ? {
            ...firstRowDefaults,
            ...registration,
            accountGoal:
              registration?.accountGoal !== undefined
                ? registration.accountGoal
                : firstRowDefaults.accountGoal,
          }
        : registration,
      index,
    ),
  );
};

export const buildStandardRegistrationPayload = (registrations = []) =>
  normalizeStandardRegistrationRows(registrations).map((registration) => ({
    accountOwner: registration.accountOwner.trim(),
    registration: registration.value.trim(),
    ...createStandardRegistrationFields(registration),
    ...(registration.accountLast4 !== undefined
      ? { accountLast4: String(registration.accountLast4 || "").trim() }
      : {}),
  }));

const isManagedMoneyMarketStrategy = (strategy) =>
  String(strategy || "")
    .toLowerCase()
    .includes("managed money market solution");

const registrationIsQualified = (registration, registrationTypes = []) =>
  registrationTypes.find((candidate) => candidate?.name === registration)?.[
    "Account Type"
  ] === "Qualified";

const strategyBondTypes = (strategy, proposalTypes = []) => {
  const proposalType = proposalTypes.find(
    (candidate) => candidate?.name === strategy,
  );
  if (!proposalType || proposalType.setBondValueBlank === true) return [];
  const types = [];
  if (
    Array.isArray(proposalType.taxableBonds) &&
    proposalType.taxableBonds.length > 0
  ) {
    types.push("Taxable");
  }
  if (
    Array.isArray(proposalType.municipalBonds) &&
    proposalType.municipalBonds.length > 0
  ) {
    types.push("Municipal");
  }
  return types;
};

export const getStandardRegistrationBondOptions = ({
  registration,
  registrationTypes = [],
  strategy,
  proposalTypes = [],
  pathStrategy = "",
}) => {
  if (
    !registration ||
    registrationIsQualified(registration, registrationTypes) ||
    isManagedMoneyMarketStrategy(strategy)
  ) {
    return [];
  }
  const primary = strategyBondTypes(strategy, proposalTypes);
  if (primary.includes("Taxable") && primary.includes("Municipal")) {
    return primary;
  }
  const path = strategyBondTypes(pathStrategy, proposalTypes);
  return path.includes("Taxable") && path.includes("Municipal") ? path : [];
};

const cleanCurrencyNumber = (value) =>
  Number(String(value || "").replace(/[^0-9.-]+/g, ""));

const isWholePercentage = (value) =>
  /^\d+$/.test(String(value ?? "")) &&
  Number(value) >= 0 &&
  Number(value) <= 100;

export const getStandardRegistrationRowIssue = (
  row = {},
  {
    strategy,
    proposalTypes = [],
    registrationTypes = [],
    pathStrategy = "",
    investmentLimits = null,
    existingRequestAction = "",
    todayIso = new Date(
      Date.now() - new Date().getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split("T")[0],
  } = {},
) => {
  if (!String(row.accountOwner || "").trim()) {
    return "Account Holder Name is required.";
  }
  if (!getStandardRegistrationValue(row).trim()) {
    return "Registration Type is required.";
  }
  const amount = cleanCurrencyNumber(row.initialInvestmentAmount);
  if (
    !Number.isFinite(amount) ||
    amount <= 0 ||
    !isInvestmentAmountWithinLimits(amount, investmentLimits)
  ) {
    return "Initial Amount of Investment is invalid.";
  }
  if (!isWholePercentage(row.accountNetWorthPct)) {
    return "% of Net Worth must be a whole number from 0 through 100.";
  }
  if (!["Yes", "No"].includes(row.accountIncomeNeed)) {
    return "Need for Income is required.";
  }
  if (row.accountIncomeNeed === "Yes") {
    const income = cleanCurrencyNumber(row.accountIncomeAmount);
    if (!Number.isFinite(income) || income <= 0 || income > amount) {
      return "Income Amount must be positive and cannot exceed the investment.";
    }
    if (!row.accountIncomeStartDate || row.accountIncomeStartDate < todayIso) {
      return "Income Start Date must be today or later.";
    }
    if (
      row.accountIncomeEndDate &&
      row.accountIncomeEndDate <= row.accountIncomeStartDate
    ) {
      return "Income End Date must be after the start date.";
    }
    if (!row.accountIncomeFrequency) return "Income Frequency is required.";
  }

  const managedMoneyMarket = isManagedMoneyMarketStrategy(strategy);
  const goals = Array.isArray(row.accountGoal)
    ? row.accountGoal
    : row.accountGoal
      ? [row.accountGoal]
      : [];
  if (goals.length === 0) return "Primary Goal is required.";
  const needsOther = managedMoneyMarket
    ? goals.includes("Other")
    : goals.includes("Funding for another goal");
  if (needsOther && !String(row.accountGoalOther || "").trim()) {
    return "Goal Explanation is required.";
  }

  const bondOptions = getStandardRegistrationBondOptions({
    registration: getStandardRegistrationValue(row),
    registrationTypes,
    strategy,
    proposalTypes,
    pathStrategy,
  });
  if (
    bondOptions.length > 1 &&
    existingRequestAction !== "Risk Tolerance Update" &&
    !bondOptions.includes(row.bondType)
  ) {
    return "Bond Type Preference is required.";
  }
  return "";
};

export const areStandardRegistrationRowsComplete = (
  rows,
  options = {},
) =>
  Array.isArray(rows) &&
  rows.length > 0 &&
  rows.every((row) => !getStandardRegistrationRowIssue(row, options));
